import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX, PMP_ALERTS_API_PREFIX } from '../utils/constants';

export type PmpAlertPayload = {
  member_id: string;
  pmp_plan_id: number;
  finding_title: string;
  finding_context: string;
  clinical_context?: string | null;
  modality_display?: string | null;
  modality_prep?: string | null;
  modality_id: number;
  full_price?: number | null;
  member_price?: number | null;
  scheduled_date: string | null;
  scheduled_month_year: string | null;
};

export type PmpAlertListParams = {
  member_id?: string;
  limit?: number;
  offset?: number;
};

export type PmpAlertItem = {
  id: string | number;
  member_id?: string;
  pmp_plan_id: number;
  pmp_reference_number: string;
  finding_title: string;
  finding_context: string;
  clinical_context?: string | null;
  modality_display?: string | null;
  modality_prep?: string | null;
  modality?: {
    id: number | string;
    name?: string;
  };
  modality_id?: number | string;
  full_price?: number | null;
  member_price?: number | null;
  scheduled_date?: string | null;
  scheduled_month_year?: string | null;
  pmp_payment_status?: string | null;
  pmp_status?: 'pending' | 'complete' | string | null;
  stripe_payment_link?: string | null;
  created_at?: string;
};

export type PmpAlertListResponse = {
  pmp_alerts: PmpAlertItem[];
  total_count?: number;
  limit?: number;
  offset?: number;
  status?: string;
};

export type PmpAlertStatus = 'pending' | 'complete';

export type PmpAlertUpdatePayload = {
  id: string | number;
  pmp_plan_id: number;
  finding_title: string;
  finding_context: string;
  clinical_context?: string | null;
  modality_display?: string | null;
  modality_prep?: string | null;
  modality_id: number;
  full_price?: number | null;
  member_price?: number | null;
  scheduled_date: string | null;
  scheduled_month_year: string | null;
  pmp_status?: PmpAlertStatus | null;
};

export const toPmpAlertUpdatePayload = (
  alert: PmpAlertItem,
  pmpStatus?: PmpAlertStatus
): PmpAlertUpdatePayload => {
  const hasDate = Boolean(alert.scheduled_date);
  return {
    id: alert.id,
    pmp_plan_id: alert.pmp_plan_id,
    finding_title: alert.finding_title,
    finding_context: alert.finding_context,
    clinical_context: alert.clinical_context,
    modality_display: alert.modality_display,
    modality_prep: alert.modality_prep,
    modality_id: Number(alert.modality?.id ?? alert.modality_id),
    full_price: alert.full_price ?? null,
    member_price: alert.member_price ?? null,
    scheduled_date: hasDate ? alert.scheduled_date ?? null : null,
    scheduled_month_year: hasDate ? null : alert.scheduled_month_year ?? null,
    pmp_status: pmpStatus ?? (alert.pmp_status === 'complete' ? 'complete' : 'pending'),
  };
};

export const createPmpAlert = async (
  payload: PmpAlertPayload
): Promise<PmpAlertItem> => {
  const { data } = await axiosInstance.post(
    `${API_V1_PREFIX}${PMP_ALERTS_API_PREFIX}`,
    payload
  );
  return data;
};

export const updatePmpAlert = async (
  payload: PmpAlertUpdatePayload
): Promise<PmpAlertItem> => {
  const { data } = await axiosInstance.patch(
    `${API_V1_PREFIX}${PMP_ALERTS_API_PREFIX}`,
    payload
  );
  return data;
};

export const deletePmpAlert = async (id: string | number): Promise<void> => {
  await axiosInstance.delete(`${API_V1_PREFIX}${PMP_ALERTS_API_PREFIX}`, {
    data: { id },
  });
};

export const getPmpAlerts = async (
  params: PmpAlertListParams = {}
): Promise<PmpAlertListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.member_id) searchParams.append('member_id', params.member_id);
  if (typeof params.limit === 'number') searchParams.append('limit', String(params.limit));
  if (typeof params.offset === 'number') searchParams.append('offset', String(params.offset));

  const query = searchParams.toString();
  const url = `${API_V1_PREFIX}${PMP_ALERTS_API_PREFIX}${query ? `?${query}` : ''}`;
  const { data } = await axiosInstance.get(url);
  return data;
};
